import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import CustomDatePicker from "./ui/date-picker";
import { useToast } from "./ui/use-toast";
import verificationService from "../services/verification";

// UK Work Permit form schema
const ukWorkPermitSchema = z.object({
  code: z
    .string()
    .min(1, "Share code is required")
    .refine(
      (val) => {
        // Remove spaces and check format
        const cleaned = val.replace(/\s/g, "");
        return /^[A-Z0-9]{9,10}$/.test(cleaned);
      },
      {
        message: "Invalid share code format. Example: W62 75N 6RM",
      }
    ),
  forename: z
    .string()
    .min(1, "First name is required")
    .min(2, "First name must be at least 2 characters"),
  surname: z
    .string()
    .min(1, "Last name is required")
    .min(2, "Last name must be at least 2 characters"),
  dob: z
    .string()
    .min(1, "Date of birth is required")
    .regex(
      /^\d{2}-\d{2}-\d{4}$/,
      "Date must be in UK format (DD-MM-YYYY)"
    ),
});

type UKWorkPermitFormData = z.infer<typeof ukWorkPermitSchema>;

interface UKWorkPermitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (result: any) => void;
  employeeId: string;
}

const UKWorkPermitModal: React.FC<UKWorkPermitModalProps> = ({
  open,
  onOpenChange,
  onSuccess,
  employeeId,
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const form = useForm<UKWorkPermitFormData>({
    resolver: zodResolver(ukWorkPermitSchema),
    defaultValues: {
      code: "",
      forename: "",
      surname: "",
      dob: "",
    },
  });

  // Watch dob value to sync with selectedDate
  const dobValue = useWatch({
    control: form.control,
    name: "dob",
  });

  // Parse DD-MM-YYYY string to Date object
  const parseDateFromString = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    return null;
  };

  // Reset date when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedDate(null);
    }
  }, [open]);

  // Sync selectedDate with dob value from form
  useEffect(() => {
    if (!dobValue) {
      setSelectedDate(null);
    } else if (!selectedDate) {
      // Only parse if selectedDate is null to avoid conflicts
      const parsed = parseDateFromString(dobValue);
      if (parsed) {
        setSelectedDate(parsed);
      }
    }
  }, [dobValue, selectedDate]);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    if (date) {
      // Convert Date to DD-MM-YYYY format
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const formatted = `${day}-${month}-${year}`;
      form.setValue("dob", formatted, { shouldValidate: true });
    } else {
      form.setValue("dob", "", { shouldValidate: true });
    }
  };

  const formatShareCode = (value: string): string => {
    // Remove all spaces and convert to uppercase
    const cleaned = value.replace(/\s/g, "").toUpperCase();

    // Format: XXX XXX XXXX or XXX XXX XXX
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    } else {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatShareCode(e.target.value);
    form.setValue("code", formatted, { shouldValidate: true });
  };

  const onSubmit = async (data: UKWorkPermitFormData) => {
    setIsSubmitting(true);
    try {
      // Call backend API which will handle the UK RTW Checker API call
      const result = await verificationService.verifyUKWorkPermit({
        employeeId,
        code: data.code.replace(/\s/g, ""),
        forename: data.forename,
        surname: data.surname,
        dob: data.dob,
      });

      if (result.code === 200) {
        toast({
          title: "✅ Right to Work Verified",
          description: result.status?.details || "Your right to work has been verified.",
        });
        onSuccess(result);
        onOpenChange(false);
        form.reset();
        setSelectedDate(null);
      } else {
        toast({
          title: "❌ Verification Failed",
          description:
            result.error || result.status?.rejected_reason || "Failed to verify right to work.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("UK RTW Check error:", error);
      toast({
        title: "❌ Error",
        description: error.message || "An error occurred while checking right to work.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>UK Work Permit Verification</DialogTitle>
          <DialogDescription>
            Please enter your details to verify your right to work in the UK. All
            information must match your passport details.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="code">
              Share Code <span className="text-destructive">*</span>
            </Label>
            <Input
              id="code"
              placeholder="W62 75N 6RM"
              {...form.register("code")}
              onChange={handleCodeChange}
              className={form.formState.errors.code ? "border-destructive" : ""}
            />
            <p className="text-xs text-muted-foreground">
              This share code must be obtained from the UK government. Format:
              XXX XXX XXX or XXX XXX XXXX (e.g., W62 75N 6RM)
            </p>
            {form.formState.errors.code && (
              <p className="text-sm text-destructive">
                {form.formState.errors.code.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="forename">
              First Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="forename"
              placeholder="John"
              {...form.register("forename")}
              className={
                form.formState.errors.forename ? "border-destructive" : ""
              }
            />
            <p className="text-xs text-muted-foreground">
              Must match the first name on your passport exactly.
            </p>
            {form.formState.errors.forename && (
              <p className="text-sm text-destructive">
                {form.formState.errors.forename.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="surname">
              Last Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="surname"
              placeholder="Doe"
              {...form.register("surname")}
              className={
                form.formState.errors.surname ? "border-destructive" : ""
              }
            />
            <p className="text-xs text-muted-foreground">
              Must match the last name on your passport exactly.
            </p>
            {form.formState.errors.surname && (
              <p className="text-sm text-destructive">
                {form.formState.errors.surname.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="dob">
              Date of Birth <span className="text-destructive">*</span>
            </Label>
            <CustomDatePicker
              selected={selectedDate}
              onChange={handleDateChange}
              placeholder="DD-MM-YYYY"
              error={!!form.formState.errors.dob}
              maxDate={new Date()} // Prevent future dates
            />
            <p className="text-xs text-muted-foreground">
              Must match the date of birth on your passport exactly. Format:
              DD-MM-YYYY
            </p>
            {form.formState.errors.dob && (
              <p className="text-sm text-destructive">
                {form.formState.errors.dob.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify Right to Work"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UKWorkPermitModal;

